/**
 * Per-division Price List page (#908) — the contract between the admin config,
 * the rows/xlsx/publish routes and the page.
 *
 * One list per DIVISION, not per raw `metadata.brand`: a product is on a
 * division's list when `isOnPriceList` is true and its brand maps to that
 * division through the live `DivisionBrandMap`.
 */
import { z } from 'zod';
import { DIVISION_IDS } from './division.types.js';
import { PRODUCT_FORMULAS } from './product-formulas.generated.js';
// ─── Roles ──────────────────────────────────────────────────────────────────
/**
 * Roles that grant no general write access. Holding ONLY these fails every
 * `requireNonViewer` / `requireInternalWriter` gate and the asset UI.
 *
 * `priceListEditor` is here on purpose: its one write path is the price-list
 * PATCH, which gates on {@link PRICE_LIST_EDIT_ROLES} itself. Without this list
 * either price-list role would pass the old `r !== 'viewer'` check and could
 * upload, replace and delete assets.
 */
export const READ_ONLY_ROLES = ['viewer', 'priceListViewer', 'priceListEditor'];
/** True when at least one role grants general write access. No roles = false. */
export function hasWritableRole(roles) {
    return roles.some((r) => !READ_ONLY_ROLES.includes(r));
}
/** Roles that may PATCH a product from the Price List page. */
export const PRICE_LIST_EDIT_ROLES = ['admin', 'pm', 'priceListEditor'];
/** Roles that edit every non-computed column, ignoring the Editable ticks. */
export const PRICE_LIST_FULL_EDIT_ROLES = ['admin', 'pm'];
// ─── Divisions ──────────────────────────────────────────────────────────────
/** The divisions that have a price list. `org` is the umbrella and `other` is unfiled products. */
export const PRICE_LIST_DIVISIONS = DIVISION_IDS.filter((id) => id !== 'org' && id !== 'other');
/**
 * Structural validation only. Whether each key is a real metadata field, and
 * whether a computed key is ticked Editable, needs the live field list and is
 * checked server-side on write.
 */
export const priceListConfigSchema = z.partialRecord(z.enum(PRICE_LIST_DIVISIONS), z
    .array(z.strictObject({ key: z.string().trim().min(1), editable: z.boolean() }))
    .refine((entries) => new Set(entries.map((e) => e.key)).size === entries.length, {
    message: 'A field may appear only once per division',
}));
/**
 * Keys carrying cost, margin or supplier — matches `COST` in the backend's
 * `price-list.service.ts`. A list showing any of these is internal: it files to
 * `PM Only`, never `Price Lists`.
 */
export const PRICE_LIST_COST_KEYS = ['actualCost', 'tariffDiscountCost', 'margin', 'supplier'];
/**
 * Keys `productService.update` recomputes on every save (compute-on-save via the
 * formula engine), so an edit to one is overwritten. They may be shown, never
 * edited. This is the static baseline; the server also treats any admin-added
 * field formula as computed.
 */
export const PRICE_LIST_COMPUTED_KEYS = Object.keys(PRODUCT_FORMULAS);
/** True when a division's columns include a cost-bearing key. */
export function priceListHasCost(entries) {
    return (entries ?? []).some((e) => PRICE_LIST_COST_KEYS.includes(e.key));
}
//# sourceMappingURL=price-list.types.js.map